import React, { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import UserLayout from "./UserLayout";
import withAuthentication from "../hoc/withAuthentication";

const RouteLoadingFallback = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "40vh",
    }}
  >
    <CircularProgress />
  </Box>
);

const DummyComponent = lazy(() => import("./DummyComponent"));
const SignIn = lazy(() => import("../pages/Auth/SignIn_updated"));
const ForgotPasswordPage = lazy(() => import("../pages/Auth/ForgotPassword"));
const VerifyEmailPage = lazy(() => import("../pages/Auth/VerifyPage"));
const PasswordResetPage = lazy(() => import("../pages/Auth/ResetPassword"));
const Register = lazy(() => import("../pages/Register/Register"));

const UserNotification = lazy(() => import("./UserNotification"));

const Homepage = lazy(() => import("../pages/LandingPage/Home"));

const ContactPage = lazy(
  () => import("../pages/LandingPage/contacts/Contactpage"),
);

const HelpPage = lazy(() => import("../components/Help/HelpPage"));
const SupportPage = lazy(() => import("../components/Support/SupportPage"));
const WalletDashboardNew = lazy(
  () => import("../components/Wallet/WalletDashboard"),
);
const WalletDashboard = lazy(
  () => import("../components/Wallet/User/UserWalletPage_redefined"),
);
const AddUsernamePage = lazy(() => import("../pages/Auth/AddUsernamePage"));
const TalentWalletDashboardNew = lazy(
  () => import("../components/Wallet/Talent/TalentWalletDashboardNew"),
);


const ComingSoon = lazy(() => import("../pages/LandingPage/ComingSoon"));

const ChatWithAITwinsPage = lazy(
  () => import("../pages/AITwins/ChatWithAITwinsPage"),
);
const ChatWithAI = lazy(() => import("../components/AITwins/ChatWithAI"));
const ChatWithAI_dynamic = lazy(
  () => import("../components/AITwins/ChatWithAI_dynamic"),
);

const PaymentSuccessPage = lazy(() => import("../pages/PaymentSuccessPage"));
const PaymentCancelPage = lazy(() => import("../pages/PaymentCancelPage"));
const KYCCompletePage = lazy(() => import("../pages/KYCCompletePage"));

const UserWorkspacePage = lazy(
  () => import("../components/UserWorkspace/UserWorkspace"),
);
const UserAITwinListPage = lazy(
  () =>
    import(
      "../components/UserWorkspace/components/AITwinChat/UserAITwinListPage"
    ),
);
const UserAITwinConversationList = lazy(
  () =>
    import(
      "../components/UserWorkspace/components/AITwinChat/UserAITwinConversationList"
    ),
);

const AIConversationPage = lazy(() =>
  import("../pages/AIConversation/AIConversationPage").then((m) => ({
    default: m.AIConversationPage,
  })),
);
const AirbankPage = lazy(
  () => import("../components/airbank/pages/AirbankPage"),
);


const AITwinListPage = lazy(() => import("../pages/AITwinListPage"));

const UserAiTProductCardsPage = lazy(
  () => import("../pages/UserAiTProductCardsPage"),
);
const UserAitCardRequestsPage = lazy(
  () => import("../pages/UserAitCardRequestsPage"),
);


const ProductAIBooth = lazy(
  () => import("../components/AIBooth/ProductAIBooth"),
);

const TNPage = lazy(
  () => import("../components/Dashboard/User/Homepage/TN/TNPage"),
);



const AuthenticatedLayout = withAuthentication(UserLayout) as React.FC<{
  children?: React.ReactNode;
}>;

const UserRoutes = () => {
  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <Routes>
        <Route path="*" element={<Navigate to="/home" replace />} />
        <Route path="/home" element={<Homepage />} />
        <Route path="/sign-in" element={<SignIn />} />
        <Route path="/coming-soon" element={<ComingSoon />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
       
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/signup/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password?/OTP" element={<VerifyEmailPage />} />
        <Route
          path="/forgot-password/reset-password"
          element={<PasswordResetPage />}
        />
        <Route path="/add-username" element={<AddUsernamePage />} />
 

        {/* Payment Result Pages */}

        {/* Main User Routes */}
        <Route path="/" element={<AuthenticatedLayout />}>


          {/* <Route path="private-hall/:id" element={<PrivateHallPage />} /> */}
          <Route path="airbank-studio" element={<TNPage />} />
          <Route path="airbank-studio" element={<TNPage />} />


          {/* Airbank - AI Twin DNA Registry */}
          <Route path="/airbank" element={<AirbankPage />} />
          <Route path="/notification" element={<UserNotification />} />
          <Route path="Wallet/traffic" element={<h1>Analytics</h1>} />
          <Route path="add-username" element={<AddUsernamePage />} />
          <Route path="about" element={<DummyComponent title="About" />} />
          <Route path="help" element={<HelpPage />} />

          {/* AI Twin Routes - Original */}
          <Route path="/ai-twins" element={<ChatWithAITwinsPage />} />

          {/* AI Twin Listing */}
          <Route
            path="/ai-twins/list"
            element={<AITwinListPage mode="user" />}
          />


          <Route
            path="/user/ait-product-cards"
            element={<UserAiTProductCardsPage />}
          />
          <Route
            path="/user/ait-card-requests"
            element={<UserAitCardRequestsPage />}
          />

          {/* AI Twin Routes - New User Interface */}
          <Route path="/user/ai-twins" element={<UserAITwinListPage />} />
          <Route path="/user/ai-twin-chat/:id" element={<ChatWithAI_dynamic />} />
          <Route
            path="/user/ai-twin-conversations"
            element={<UserAITwinConversationList />}
          />

          {/* Original AI Twin Chat Route - Keep as is */}
          <Route path="ai-twin-chat/:id" element={<ChatWithAI />} />
          <Route path="/checkout/success" element={<PaymentSuccessPage />} />
          <Route path="/checkout/cancel" element={<PaymentCancelPage />} />
          <Route path="/complete" element={<KYCCompletePage />} />

          <Route path="ai-twin-chat/:id" element={<ChatWithAI />} />


          <Route path="userWallet" element={<WalletDashboardNew />} />
          <Route path="wallet" element={<WalletDashboard />} />
          <Route
            path="ai-conversation/:channel_name"
            element={<AIConversationPage />}
          />

          <Route path="wallet/:id" element={<TalentWalletDashboardNew />} />
          <Route path="product/ai-booth/:id" element={<ProductAIBooth />} />





          <Route path="/user-workspace" element={<UserWorkspacePage />} />

          <Route path="*" element={<Navigate to="/home" replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
};

export default UserRoutes;
